import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, ChevronRight, FolderKanban } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";

interface MyAllocationData {
  weekStartDate: string;
  capacity: number;
  allocatedHours: number;
  allocationPercent: number;
  projects: Array<{
    projectId: number;
    projectName: string;
    hours: number;
  }>;
}

interface MyAllocationWidgetProps {
  allocation: MyAllocationData;
}

export default function MyAllocationWidget({ allocation }: MyAllocationWidgetProps) {
  const [, navigate] = useLocation();
  const pct = allocation.allocationPercent;

  const getStatusColor = (pct: number) => {
    if (pct > 100) return { stroke: "#ef4444", bg: "bg-red-50 dark:bg-red-950/30", text: "text-red-600 dark:text-red-400" };
    if (pct > 80) return { stroke: "#f59e0b", bg: "bg-amber-50 dark:bg-amber-950/30", text: "text-amber-600 dark:text-amber-400" };
    return { stroke: "#6366f1", bg: "bg-indigo-50 dark:bg-indigo-950/30", text: "text-indigo-600 dark:text-indigo-400" };
  };

  const statusColor = getStatusColor(pct);

  return (
    <Card className="h-full border-0 shadow-lg bg-card">
      <CardHeader className="pb-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md">
              <PieChart className="h-5 w-5 text-white" />
            </div>
            <SectionHelp contentId="dashboard:allocation" tooltip="What is allocation?">
              <CardTitle className="text-lg font-bold">My Allocation</CardTitle>
            </SectionHelp>
          </div>
          <span className="text-xs font-semibold text-muted-foreground bg-muted px-2 py-1 rounded-md">This Week</span>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        <div className="flex items-center gap-6">
          {/* Circular Progress */}
          <div className="relative w-24 h-24 flex-shrink-0">
            <svg className="w-24 h-24 transform -rotate-90">
              <circle
                className="text-muted"
                strokeWidth="8"
                stroke="currentColor"
                fill="transparent"
                r="40"
                cx="48"
                cy="48"
              />
              <circle
                style={{ stroke: statusColor.stroke }}
                strokeWidth="8"
                strokeDasharray={`${Math.min(pct, 100) * 2.51} 251`}
                strokeLinecap="round"
                fill="transparent"
                r="40"
                cx="48"
                cy="48"
                className="transition-all duration-700 ease-out"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn("text-2xl font-bold", statusColor.text)}>
                {pct}%
              </span>
            </div>
          </div>
          
          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className={cn("inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold mb-3", statusColor.bg, statusColor.text)}>
              {allocation.allocatedHours}h of {allocation.capacity}h
            </div>
            {allocation.projects.length === 0 ? (
              <div className="text-sm text-muted-foreground">No allocations this week</div>
            ) : (
              <div className="space-y-2">
                {allocation.projects.slice(0, 3).map((p) => (
                  <div key={p.projectId} className="flex items-center justify-between text-sm">
                    <span className="text-foreground truncate flex-1 flex items-center gap-2">
                      <FolderKanban className="h-3 w-3 text-muted-foreground" />
                      {p.projectName}
                    </span>
                    <span className="text-foreground font-semibold ml-2 bg-muted px-2 py-0.5 rounded">{p.hours}h</span>
                  </div>
                ))}
                {allocation.projects.length > 3 && (
                  <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium cursor-pointer hover:text-indigo-700 dark:hover:text-indigo-300">
                    +{allocation.projects.length - 3} more projects
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
